
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, RouterLink, NavigationEnd, PRIMARY_OUTLET } from '@angular/router';
import { filter } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';
import { LPMI_ROUTES } from '../../constants/routes';
import { IconComponent } from "../general/icon/icon.component";
import { CacheService } from '../../services/cache.service';
import { CACHE_KEYS } from '../../constants/cache-keys';

interface Crumb {
  PAGE: string;
  PATH: string;
  isLast?: boolean;
}

@Component({
  selector: 'nuc-breadcrumb',
  imports: [CommonModule, RouterLink, MatMenuModule, IconComponent],
  standalone: true,
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss'
})
export class BreadcrumbComponent implements OnInit {
  breadcrumbs: Crumb[] = [];
  showBreadCrumbs = true;

  constructor(private router: Router, private route: ActivatedRoute, private renderer: Renderer2,
    private cacheService: CacheService
  ) {}

  ngOnInit() {
    this.init();
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.init());
  }

  private init(): void {
    // Preserve your existing visibility check
    this.showBreadCrumbs = this.route.firstChild?.snapshot.routeConfig?.path != LPMI_ROUTES.LANDING.PATH;

    // *** Targeted enhancement: only for help area article path ***
    const hasHelpAreaArticle = this.routePathIncludes(LPMI_ROUTES.HELP_AREA_INFO_ARTICLE.PATH);
    const appName = this.cacheService.getFromCache(CACHE_KEYS.ARTICLE.APP_NAME);
    if (hasHelpAreaArticle) {
      // Construct the 3-level chain safely
      const home: Crumb = { PAGE: LPMI_ROUTES.LANDING.PAGE, PATH: LPMI_ROUTES.LANDING.PATH };
      const helpArea: Crumb = {
        PAGE: LPMI_ROUTES.HELP_AREA_INFO.PAGE,
        PATH: `/${LPMI_ROUTES.HELP_AREA_INFO.PATH}/${appName}`
      };
      const article: Crumb = {
        PAGE: LPMI_ROUTES.HELP_AREA_INFO_ARTICLE.PAGE,
        PATH: `/${LPMI_ROUTES.HELP_AREA_INFO.PATH}/${LPMI_ROUTES.HELP_AREA_INFO_ARTICLE.PATH}`
      };

      const crumbs = [home, helpArea, article];
      crumbs.forEach((c, i) => (c.isLast = i === crumbs.length - 1));
      this.breadcrumbs = crumbs;
      return; // important: do not touch other flows
    }

    // *** Fallback: your current behavior (unchanged) ***
    if (this.showBreadCrumbs) {
      const data = this.route.firstChild?.snapshot.data?.['breadcrumb'];
      // If your data is already an array of {PAGE, PATH}, assign directly
      if (Array.isArray(data)) {
        const crumbs = data as Crumb[];
        crumbs.forEach((c, i) => (c.isLast = i === crumbs.length - 1));
        this.breadcrumbs = crumbs;
      } else if (data) {
        // Or normalize if it's a single object/string
        this.breadcrumbs = [{ PAGE: data, PATH: this.router.url, isLast: true }];
      } else {
        this.breadcrumbs = [];
      }
    } else {
      this.breadcrumbs = [];
    }
  }

  // Utility: check if current activated route tree contains a given path segment
  private routePathIncludes(segment: string): boolean {
    let current: ActivatedRoute | null = this.route.root;
    while (current) {
      for (const child of current.children) {
        if (child.outlet !== PRIMARY_OUTLET) continue;
        const path = child.routeConfig?.path ?? '';
        if (path.split('/').includes(segment)) return true;
        current = child;
        break;
      }
      // if there are no PRIMARY_OUTLET children, break
      if (!current || current.children.length === 0) break;
    }
    // Final check on URL as a fallback
    return this.router.url.includes(`/${segment}`);
  }
}
